import { useState } from 'react';
import { Building2, Loader2, LogOut } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

/**
 * Shown instead of the app to a signed-in account that belongs to no
 * organization and is neither a superuser nor Jeket staff (JSS#849
 * follow-up). The BFF refuses such an account every org-scoped call with 403
 * `no_organization`; this says why, and who can fix it, instead of a console
 * of empty pages and errors. Sign-in itself is not refused: the account holder
 * cannot add themselves to an organization, so they are told who can.
 */
export default function NoOrganization() {
  const { user, logout } = useAuth();
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await logout();
    } catch {
      setSigningOut(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-deep flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Jeket" className="h-12 mx-auto mb-2" />
        </div>
        <div className="bg-surface border border-gold/20 rounded-2xl p-8 shadow-jeket text-center">
          <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center mx-auto mb-4">
            <Building2 size={24} className="text-gold" />
          </div>
          <h2 className="text-xl font-semibold text-silver-bright">
            Your account isn&apos;t part of an organization yet
          </h2>
          <p className="text-sm text-silver-muted mt-3">
            Contact your administrator and ask them to add you to your organization. Once they have,
            sign out and sign in again.
          </p>
          {(user?.email || user?.username) && (
            <p className="text-xs text-silver-muted mt-4">
              Signed in as <span className="text-silver-bright">{user.email ?? user.username}</span>
            </p>
          )}
          <button
            onClick={handleSignOut}
            disabled={signingOut}
            className={cn(
              'mt-6 w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-semibold transition-all',
              signingOut
                ? 'bg-gold/50 text-surface-deep cursor-not-allowed'
                : 'bg-gold hover:bg-gold-light text-surface-deep hover:shadow-jeket-gold',
            )}
          >
            {signingOut ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
